import usePageMeta from "@/lib/usePageMeta";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, CaretLeft, Check, PaintBucket } from "@phosphor-icons/react";
import { toast } from "sonner";
import FeaturedTestimonial from "@/components/FeaturedTestimonial";
import StockAlertForm from "@/components/StockAlertForm";
import WatchSizeButton from "@/components/WatchSizeButton";
import { useCustomer } from "@/context/CustomerContext";
import ProductReviews from "@/components/reviews/ProductReviews";
import HoverMagnifier from "@/components/HoverMagnifier";
import PhotoViewer from "@/components/PhotoViewer";
import CustomerPhotos from "@/components/reviews/CustomerPhotos";
import WishlistButton from "@/components/WishlistButton";
import ShareMenu from "@/components/ShareMenu";
import { DesignStrip } from "@/components/DesignStrip";

const RESTOCK_BADGE_MS = 7 * 24 * 60 * 60 * 1000;

// Sanitizer config — allow typical Printify description markup (p, h1-6, ul/ol/li, strong/em,
// anchors, line breaks, size-chart tables), block scripts/handlers/iframes/forms.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "span", "div",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "strong", "b", "em", "i", "u",
    "a",
    "table", "thead", "tbody", "tfoot", "tr", "th", "td",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "colspan", "rowspan"],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "style"],
};

function sanitize(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

// Common Printify color names → hex, used only when Printify didn't provide a hex.
const COLOR_MAP = {
  black: "#111111", white: "#ffffff", "off white": "#f5f1e6", cream: "#f1ead2", ivory: "#f6f0e0",
  gray: "#8a8a8a", grey: "#8a8a8a", "heather gray": "#9a9ea1", "heather grey": "#9a9ea1",
  charcoal: "#36454f", "dark gray": "#555555", "dark grey": "#555555", "light gray": "#cccccc", "light grey": "#cccccc",
  silver: "#c0c0c0", "sport grey": "#9a9ea1", "ash grey": "#b2beb5", "ash gray": "#b2beb5",
  navy: "#1b2a4e", "navy blue": "#1b2a4e", "royal blue": "#1a3fcc", blue: "#2c6ef2",
  "light blue": "#89c2ff", "sky blue": "#87ceeb", teal: "#008080", turquoise: "#30d5c8", aqua: "#4bd0d6",
  red: "#d13030", "cardinal red": "#b02133", maroon: "#800020", burgundy: "#6a0b2a", wine: "#5c1727",
  orange: "#f28534", "burnt orange": "#cc5500", coral: "#ff7f50", salmon: "#fa8072", peach: "#ffd1b2",
  yellow: "#f5c518", gold: "#d4af37", mustard: "#c9a227",
  green: "#2f8f3e", "forest green": "#245c36", "military green": "#4b5320", olive: "#808000",
  "army green": "#4b5320", lime: "#b3ff66", mint: "#aaf0d1", sage: "#9caf88", "kelly green": "#4cbb17",
  pink: "#f49ac2", "hot pink": "#ff69b4", fuchsia: "#ff007f", magenta: "#ff00ff",
  purple: "#7a4ba3", violet: "#8f00ff", lavender: "#b497d6", plum: "#8e4585",
  brown: "#7a4b1a", tan: "#d2b48c", khaki: "#c3b091", sand: "#e5d6b3", beige: "#f5f5dc",
  "dusty rose": "#dcae96", "dusty blue": "#8aa3b8",
  forest: "#245c36", "midnight navy": "#0a1a3f", midnight: "#0a0a2a", "deep teal": "#003c43",
  "heather navy": "#2b3a55",
};

const SIZE_ORDER = [
  "xxs", "xs", "s", "m", "l", "xl", "xxl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl",
  "one size", "osfa",
];

function sortOptionValues(values, type) {
  const copy = [...values];
  if (type === "size") {
    copy.sort((a, b) => {
      const an = String(a.value).toLowerCase().trim();
      const bn = String(b.value).toLowerCase().trim();
      const ai = SIZE_ORDER.indexOf(an);
      const bi = SIZE_ORDER.indexOf(bn);
      // Known sizes first in canonical order
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      // Numeric sizes (e.g., "28", "11 oz") → numeric sort
      const na = parseFloat(an);
      const nb = parseFloat(bn);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return an.localeCompare(bn);
    });
  }
  return copy;
}

const SIZE_PATTERN = /^(xxs|xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl|6xl|one size|\d+\s?(?:xl)?|\d+(?:oz|cm|in|inch|ml|mm))$/i;

function colorSwatch(label) {
  if (!label) return null;
  const key = String(label).toLowerCase().trim();
  if (COLOR_MAP[key]) return COLOR_MAP[key];
  for (const name of Object.keys(COLOR_MAP)) {
    if (key === name || key.endsWith(" " + name) || key.startsWith(name + " ") || key.includes(name)) {
      return COLOR_MAP[name];
    }
  }
  return null;
}

function inferOptionType(value) {
  if (!value) return "unknown";
  if (SIZE_PATTERN.test(String(value).trim())) return "size";
  if (colorSwatch(value)) return "color";
  return "unknown";
}

/**
 * Return a list of { name, type, value, hex } for a variant.
 * Prefers resolved option_values from backend; falls back to parsing title "Black / S".
 */
function readVariantOptions(variant) {
  if (Array.isArray(variant.option_values) && variant.option_values.length) {
    return variant.option_values.map((ov) => ({
      name: ov.name || (ov.type ? ov.type[0].toUpperCase() + ov.type.slice(1) : "Option"),
      type: ov.type || inferOptionType(ov.value),
      value: String(ov.value ?? ""),
      hex: ov.hex || null,
    }));
  }
  // Fallback: parse title like "2XL / Black" or "Black / S"
  const parts = String(variant.title || "")
    .split(" / ")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.map((part) => {
    const type = inferOptionType(part);
    return {
      name: type === "size" ? "Size" : type === "color" ? "Color" : "Option",
      type,
      value: part,
      hex: null,
    };
  });
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const { customer } = useCustomer();
  usePageMeta({ title: product ? `${product.title.trim()} — Funny Fishing Gift` : "", description: product?.description, path: `/product/${id}` });
  const [selected, setSelected] = useState(null);
  // canonical option order (by name) so every variant's parts line up, whatever order Printify sent
  const optionRank = useMemo(() => {
    const names = (product?.product_options || []).map((o) => o.name);
    const first = (product?.variants || []).find((v) => v.is_enabled);
    if (!names.length && first) readVariantOptions(first).forEach((p) => names.push(p.name));
    return Object.fromEntries(names.map((n, i) => [n, i]));
  }, [product]);
  const partsOf = useCallback(
    (v) => [...readVariantOptions(v)].sort((a, b) => (optionRank[a.name] ?? 99) - (optionRank[b.name] ?? 99)),
    [optionRank]
  );
  const { add } = useCart();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        const vs = r.data.variants || [];
        const firstEnabled = vs.find((v) => v.is_enabled && v.is_available !== false) || vs.find((v) => v.is_enabled) || vs[0];
        setSelected(firstEnabled || null);
      })
      .catch(() => setProduct(false))
      .finally(() => setLoading(false));
  }, [id]);

  // When the selected variant changes, auto-switch the main image to one
  // tagged with that variant_id (so the hero image matches the chosen color).
  useEffect(() => {
    if (!selected || !product?.images?.length) return;
    const idx = product.images.findIndex(
      (img) => Array.isArray(img.variant_ids) && img.variant_ids.includes(selected.id)
    );
    if (idx >= 0) setActiveImage(idx);
  }, [selected, product]);

  // Build groups of { name, type, values: [{value, hex}] } across all enabled variants
  const optionGroups = useMemo(() => {
    if (!product?.variants) return [];
    const groupMap = new Map();
    for (const v of product.variants) {
      if (!v.is_enabled) continue;
      const parts = partsOf(v);
      parts.forEach((p, idx) => {
        const key = `${idx}:${p.name}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, { idx, name: p.name, type: p.type, values: new Map() });
        }
        const g = groupMap.get(key);
        if (!g.values.has(p.value)) g.values.set(p.value, { value: p.value, hex: p.hex });
      });
    }
    return Array.from(groupMap.values()).map((g) => ({
      ...g,
      values: sortOptionValues(Array.from(g.values.values()), g.type),
    }));
  }, [product, partsOf]);

  const selectedParts = useMemo(() => (selected ? partsOf(selected) : []), [selected, partsOf]);

  // value is in stock with the other current picks / in stock in any combination
  const inStockVariants = useMemo(
    () => (product?.variants || [])
      .filter((v) => v.is_enabled && v.is_available !== false)
      .map((v) => ({ parts: partsOf(v), fresh: v.restocked_at && Date.now() - new Date(v.restocked_at).getTime() < RESTOCK_BADGE_MS })),
    [product, partsOf]
  );
  const matchesPicks = (parts, groupIdx) =>
    parts.every((p, i) => i === groupIdx || !selectedParts[i] || p.value === selectedParts[i].value);
  const stockState = (groupIdx, value) => {
    const withValue = inStockVariants.filter((x) => x.parts[groupIdx]?.value === value);
    if (!withValue.length) return "none";
    return withValue.some((x) => matchesPicks(x.parts, groupIdx)) ? "ok" : "combo";
  };
  const isFreshRestock = (groupIdx, value) =>
    inStockVariants.some((x) => x.fresh && x.parts[groupIdx]?.value === value && matchesPicks(x.parts, groupIdx));
  const selectedSoldOut = selected && selected.is_available === false;

  if (loading) return <div className="min-h-[60vh] mx-auto max-w-7xl px-6 py-20">Loading…</div>;
  if (!product) return (
    <div className="mx-auto max-w-7xl px-6 py-20">
      <p className="text-sm text-muted-foreground">Product not found.</p>
      <Link to="/shop" className="text-[#C84A31] underline">Back to shop</Link>
    </div>
  );

  const images = product.images?.length ? product.images : (product.default_image ? [{ src: product.default_image }] : []);
  const currentImg = images[activeImage]?.src || product.default_image;

  function selectVariantByOption(groupIdx, value) {
    // Find a variant whose option at position groupIdx has this value,
    // keeping all other currently-selected positions when possible.
    const currentParts = selectedParts;
    const usable = (v) => v.is_enabled && v.is_available !== false;
    const exact = product.variants.find((v) => {
      if (!v.is_enabled) return false;
      const parts = partsOf(v);
      return parts.every((p, i) => (i === groupIdx ? p.value === value : !currentParts[i] || p.value === currentParts[i].value));
    });
    if (exact) return setSelected(exact);
    const match = product.variants.find((v) => {
      if (!usable(v)) return false;
      const parts = partsOf(v);
      return parts.every((p, i) => {
        if (i === groupIdx) return p.value === value;
        return currentParts[i] ? p.value === currentParts[i].value : true;
      });
    }) || product.variants.find((v) => {
      if (!usable(v)) return false;
      const parts = partsOf(v);
      return parts[groupIdx]?.value === value;
    });
    if (match) setSelected(match);
  }

  function handleAddToCart() {
    if (!selected) return toast.error("Select a variant first");
    if (selectedSoldOut) return toast.error("That option is sold out right now");
    add(product, selected);
    toast.success(`${product.title} added to cart`);
  }

  return (
    <div data-testid="product-detail" className="mx-auto max-w-7xl px-6 py-10">
      <Link to="/shop" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-6">
        <CaretLeft size={14} /> Back to shop
      </Link>

      <FeaturedTestimonial />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* GALLERY */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="aspect-square bg-muted rounded-sm overflow-hidden border border-border">
            {currentImg && (
              <HoverMagnifier src={currentImg} alt={product.title} onOpen={() => setViewerOpen(true)} />
            )}
          </div>
          {viewerOpen && (
            <PhotoViewer photos={images.map((img) => ({ src: img.src, alt: product.title }))}
              index={activeImage} onIndex={setActiveImage} onClose={() => setViewerOpen(false)} />
          )}
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {images.slice(0, 10).map((img, i) => (
                <button
                  key={img.src || i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square rounded-sm overflow-hidden border-2 transition-colors ${
                    activeImage === i ? "border-[#C84A31]" : "border-transparent hover:border-border"
                  }`}
                >
                  <img src={img.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <CustomerPhotos productId={product.id} />
        </div>

        {/* INFO */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{product.category}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">{product.title}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span data-testid="product-price" className="font-display text-3xl text-[#C84A31]">{formatPrice(selected?.price ?? product.min_price)}</span>
            {product.min_price != null && selected?.price !== product.min_price && (
              <span className="text-sm text-muted-foreground">from {formatPrice(product.min_price)}</span>
            )}
          </div>

          <div className="mt-8 space-y-8">
            {optionGroups.map((g) => {
              const selectedValue = selectedParts[g.idx]?.value;
              const isColor = g.type === "color";
              return (
                <div key={`${g.idx}-${g.name}`} data-testid={`option-${g.name}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                      {g.name}
                    </div>
                    {selectedValue != null && (
                      <div
                        data-testid={`option-${g.name}-selected`}
                        className="font-display text-lg md:text-xl text-foreground"
                      >
                        {selectedValue}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {g.values.map((val) => {
                      const active = selectedValue === val.value;
                      const stock = stockState(g.idx, val.value);
                      const out = stock !== "ok";
                      const fresh = !out && isFreshRestock(g.idx, val.value);
                      const swatchHex = isColor ? (val.hex || colorSwatch(val.value)) : null;
                      if (isColor && swatchHex) {
                        return (
                          <button
                            key={val.value}
                            data-testid={`option-${g.name}-${val.value}`}
                            onClick={() => selectVariantByOption(g.idx, val.value)}
                            data-stock={stock}
                            title={out ? `${val.value} (sold out${stock === "combo" ? " in this size" : ""})` : val.value}
                            aria-label={out ? `${val.value}, sold out` : val.value}
                            aria-pressed={active}
                            className={`relative w-11 h-11 rounded-full border-2 transition-all ${
                              active
                                ? "border-[#C84A31] shadow-[0_0_0_3px_rgba(200,74,49,0.18)]"
                                : "border-border hover:border-foreground"
                            } ${out ? "opacity-45" : ""}`}
                            style={{ backgroundColor: swatchHex }}
                          >
                            {out && <SoldOutSlash inset={16} />}
                            {fresh && <BackBadge testid={`back-badge-${g.name}-${val.value}`} />}
                            {active && (
                              <Check
                                size={18}
                                weight="bold"
                                className="absolute inset-0 m-auto"
                                style={{
                                  color: ["#ffffff", "#f5f1e6", "#f1ead2", "#f6f0e0", "#f5f5dc", "#ffd1b2", "#aaf0d1", "#b3ff66", "#e5d6b3"].includes(swatchHex) ? "#111" : "#fff",
                                }}
                              />
                            )}
                          </button>
                        );
                      }
                      return (
                        <button
                          key={val.value}
                          data-testid={`option-${g.name}-${val.value}`}
                          onClick={() => selectVariantByOption(g.idx, val.value)}
                          data-stock={stock}
                          title={out ? `${val.value} is sold out${stock === "combo" ? " in this color" : ""}` : undefined}
                          aria-label={out ? `${val.value}, sold out` : val.value}
                          aria-pressed={active}
                          className={`relative inline-flex items-center justify-center gap-1.5 min-w-[52px] px-4 h-11 text-base rounded-sm border transition-colors ${
                            active
                              ? "bg-[#1C211E] text-white border-[#1C211E]"
                              : "bg-white border-border hover:border-foreground"
                          } ${out ? "text-muted-foreground opacity-60" : ""}`}
                        >
                          {out && <SoldOutSlash />}
                          {fresh && <BackBadge testid={`back-badge-${g.name}-${val.value}`} />}
                          {active && <Check size={14} weight="bold" />}
                          <span className={`font-display ${out ? "line-through" : ""}`}>{val.value}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedSoldOut && (
            <div data-testid="sold-out-note" className="mt-8 text-sm text-[#C84A31] bg-[#C84A31]/5 border border-[#C84A31]/30 rounded-sm px-4 py-3">
              This option is sold out right now. Tap another size or color, or get an alert when it's back.
              {customer ? <WatchSizeButton productId={product.id} variant={selected} /> : (
                <>
                  <StockAlertForm productId={product.id} variant={selected} />
                  <WatchSizeButton productId={product.id} variant={selected} />
                </>
              )}
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={selectedSoldOut}
              className="btn-primary rounded-sm h-12 flex-1 font-display tracking-wide"
            >
              <ShoppingBag size={18} className="mr-2" /> {selectedSoldOut ? "Sold out" : "Add to Cart"}
            </Button>
            <div className="flex gap-3 [&>*]:flex-1 sm:[&>*]:flex-none">
              <WishlistButton productId={product.id} variant="inline" />
              <ShareMenu testId="product-share" url={`${window.location.origin}/api/p/${product.id}`} image={product.default_image}
                title={product.title?.trim()} text={`Check out "${product.title?.trim()}" from Reel Funny Goods`} />
            </div>
          </div>

          {/* Custom color-request note — shown on every product */}
          <div
            data-testid="custom-color-note"
            className="mt-6 rounded-sm border border-[#C84A31]/30 bg-[#C84A31]/5 p-4 flex gap-3"
          >
            <PaintBucket size={20} weight="duotone" className="text-[#C84A31] flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <div className="font-display text-foreground mb-1">Want a different background color?</div>
              <p className="text-foreground/80">
                Reach out and I can make a new listing with a revised background color
                (within my ability — <strong>solid colors only</strong>).
                Shoot me a quick message at{" "}
                <a
                  href="mailto:orders@reelfunnygoods.com?subject=Custom%20color%20request"
                  className="text-[#C84A31] underline underline-offset-4"
                >
                  orders@reelfunnygoods.com
                </a>
                {" "}with the design and the color you want.
              </p>
            </div>
          </div>

          <div className="mt-10 product-description text-foreground/85 leading-relaxed">
            <p data-testid="color-variance-notice" className="flex items-start gap-2 text-sm bg-[#F59E0B]/10 border-l-2 border-[#F59E0B] px-3 py-2 rounded-sm mb-5 text-foreground/80">
              <PaintBucket size={16} className="text-[#C84A31] shrink-0 mt-0.5" />
              <span><strong>Please note:</strong> colors may vary slightly from the product image shown, depending on your screen and the printing process.</span>
            </p>
            {product.description && <div dangerouslySetInnerHTML={{ __html: sanitize(product.description) }} />}
          </div>
        </div>
      </div>
      <DesignStrip productId={product.id} />
      <ProductReviews product={product} />
    </div>
  );
}

function SoldOutSlash({ inset = 4 }) {
  return (
    <svg aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
      <line x1={inset} y1={100 - inset} x2={100 - inset} y2={inset} stroke="#C84A31" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function BackBadge({ testid }) {
  return (
    <span
      data-testid={testid}
      className="absolute -top-2 -right-2 z-10 whitespace-nowrap rounded-full bg-[#2A4B3C] text-white text-[9px] font-display uppercase tracking-wider px-1.5 py-0.5 shadow-sm animate-in zoom-in duration-300"
    >
      Back!
    </span>
  );
}
